import { Alert, Platform } from 'react-native'
import { PLAY_STORE_URL } from './checkStoreVersion'

/**
 * Branch link domain. Every native build uses the live Branch key, so the live domain
 * is the one the app can attribute, whatever the stage.
 */
const BRANCH_LINK_DOMAIN = 'https://gjva8.app.link'
/** Same listing as `APP_STORE_URL`, without the locale and slug, to keep the QR code small. */
const SHORT_APP_STORE_URL = 'https://apps.apple.com/app/id1559404571'

/** `/` and `:` are legal in a query value; leaving them unescaped keeps the QR code small. */
function encodeQueryValue(value: string) {
  return encodeURIComponent(value).replace(/%2F/gi, '/').replace(/%3A/gi, ':')
}

/**
 * Utility functions for generating share URLs
 * Handles different environments (dev, staging, prod)
 */

/**
 * Get the base domain for share URLs based on environment
 */
function getProductionDomain(): string {
  const isProd = process.env.EXPO_PUBLIC_STAGE === 'prod'
  return isProd ? 'https://app.pokerpower.com' : 'https://app.dev.pokerpower.com'
}

/**
 * Get the base URL for share/QR links. Use on web (window.origin) or mobile (app domain).
 */
export function getShareBaseUrl(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.origin
  }
  return getProductionDomain()
}

function getLocalhostDomain(): string {
  return 'http://localhost:8081'
}

/**
 * Generate a share URL for joining a game by code
 * In production, always uses the universal link domain
 * In __DEV__, defaults to the universal link (works for mobile)
 *
 * @param code - The 6-character game code
 * @returns Full URL for sharing
 */
export function getShareUrl(code: string): string {
  // Always use production/staging domain - works as universal link on mobile
  const domain = getProductionDomain()
  return `${domain}/game/join/${code}`
}

/**
 * Generate the share link (and QR code) for joining a lounge.
 *
 * A Branch long link, built locally with no API call, so it takes the user into the lounge
 * from every starting point:
 * - app installed: the universal/app link opens the app on `$deeplink_path`
 * - app not installed: App Store / Play Store, then Branch hands `$deeplink_path` to the
 *   app on first open (see `referBranchSubscription`)
 * - desktop: the web app's join page
 *
 * @param code - The lounge join code
 * @returns Full URL for sharing
 */
export function getLoungeShareUrl(code: string): string {
  const params: Record<string, string> = {
    $deeplink_path: `lounges/join?code=${code}`,
    $ios_url: SHORT_APP_STORE_URL,
    $android_url: PLAY_STORE_URL,
    $desktop_url: getLoungeJoinUrl(code),
    feature: 'lounge_join'
  }
  const query = Object.entries(params)
    .map(([key, value]) => `${key}=${encodeQueryValue(value)}`)
    .join('&')
  return `${BRANCH_LINK_DOMAIN}/?${query}`
}

/**
 * Generate a localhost share URL for web development
 * @param code - The 6-character game code
 * @returns Full localhost URL for sharing
 */
export function getLocalhostShareUrl(code: string): string {
  return `${getLocalhostDomain()}/game/join/${code}`
}

/**
 * In __DEV__ mode, prompts the user to choose between web (localhost) or mobile (universal link) URL
 * In production, returns the universal link URL directly
 *
 * @param code - The 6-character game code
 * @returns Promise that resolves to the appropriate share URL
 */
export function promptShareUrl(code: string): Promise<string> {
  return new Promise((resolve) => {
    if (!__DEV__) {
      // In production, always use universal link
      resolve(getShareUrl(code))
      return
    }

    // In __DEV__ on web, always use localhost
    if (Platform.OS === 'web') {
      resolve(getLocalhostShareUrl(code))
      return
    }

    // In __DEV__ on mobile, ask user
    Alert.alert(
      'Share Link Type',
      'Who are you sharing with?',
      [
        {
          text: 'Mobile App',
          onPress: () => resolve(getShareUrl(code))
        },
        {
          text: 'Web (localhost)',
          onPress: () => resolve(getLocalhostShareUrl(code))
        }
      ],
      { cancelable: false }
    )
  })
}

/**
 * Generate a share message with code and URL
 * @param code - The 6-character game code
 * @returns Object with message and URL for sharing
 */
export function getShareContent(code: string) {
  const url = getShareUrl(code)
  return {
    url,
    message: `${code}: Join my game on Poker Power`,
    title: 'Join my Poker Power game'
  }
}

/**
 * The web app's join page for a lounge. Also where the Branch share link sends desktop users.
 *
 * @param code - The lounge join code
 * @returns Full URL of the web join page
 */
export function getLoungeJoinUrl(code: string): string {
  const domain = getProductionDomain()
  return `${domain}/lounges/join?code=${code}`
}
